import { useEffect, useRef, useState } from 'react';
import type { Prospect } from '@prototypical/types';
import { api } from '../../../lib/api';
import { friendlyError } from '../../../lib/friendlyError';
import { collectHarvestDetails, harvestExport, saveDownload, type ExportScope } from './savedHarvests';

export default function SavedHarvestActions({ saved, checked, complete, onUpdated }: {
  saved: Prospect[]; checked: string[]; complete: boolean; onUpdated: (prospect: Prospect) => void;
}) {
  const [scope, setScope] = useState<ExportScope>('all');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [progress, setProgress] = useState<{ action: string; done: number; total: number } | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), []);
  const selected = saved.filter(prospect => checked.includes(prospect.placeId));
  const missing = saved.filter(prospect => !prospect.location);
  const missingSelected = selected.filter(prospect => !prospect.location);
  const targets = scope === 'all' ? saved : selected;

  async function download() {
    if (active.current) return;
    const controller = new AbortController(); active.current = controller;
    setError(''); setMessage(''); setProgress({ action: 'Preparing download', done: 0, total: targets.length });
    try {
      const details = await collectHarvestDetails(targets, api.prospectHarvestDetail, controller.signal, done => setProgress({ action: 'Preparing download', done, total: targets.length }));
      saveDownload(harvestExport(details, format, scope), `pbf-harvests-${scope}-${new Date().toISOString().slice(0, 10)}.${format}`);
      setMessage(`Downloaded ${details.length} saved ${details.length === 1 ? 'business' : 'businesses'} as ${format.toUpperCase()}.`);
    } catch (reason) {
      if (!controller.signal.aborted) setError(`${friendlyError(reason, 'The download could not finish. Please retry.')} No partial file was downloaded.`);
    } finally {
      if (active.current === controller) { active.current = null; setProgress(null); }
    }
  }

  async function resolve(prospects: Prospect[]) {
    if (active.current) return;
    const controller = new AbortController(); active.current = controller;
    let resolved = 0; const failures: string[] = [];
    setError(''); setMessage('');
    try {
      for (let index = 0; index < prospects.length && !controller.signal.aborted; index++) {
        const prospect = prospects[index];
        setProgress({ action: `Finding location for ${prospect.name}`, done: index, total: prospects.length });
        try {
          const updated = await api.resolveProspectLocation(prospect.placeId, controller.signal);
          if (controller.signal.aborted) break;
          onUpdated(updated);
          if (updated.location) resolved++; else failures.push(prospect.name);
        } catch (reason) { if (!controller.signal.aborted) failures.push(`${prospect.name}: ${friendlyError(reason, 'location unavailable')}`); }
      }
      if (!controller.signal.aborted) {
        setMessage(`Added ${resolved} map ${resolved === 1 ? 'location' : 'locations'}.`);
        if (failures.length) setError(`${failures.length} locations could not be resolved. ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '; more businesses remain without locations.' : ''}`);
      }
    } finally { if (active.current === controller) { active.current = null; setProgress(null); } }
  }
  return <section className="prospect-saved-actions" aria-label="Saved harvest actions">
    <div className="prospect-export-controls">
      <label>Download scope<select value={scope} onChange={event => setScope(event.target.value as ExportScope)} disabled={!!progress}><option value="all">All saved businesses ({saved.length}{complete ? '' : ' loaded'})</option><option value="selected">Selected businesses ({selected.length})</option></select></label>
      <label>File format<select value={format} onChange={event => setFormat(event.target.value as 'csv' | 'json')} disabled={!!progress}><option value="csv">CSV spreadsheet</option><option value="json">Full JSON</option></select></label>
      <button className="btn primary" disabled={!!progress || !targets.length || !complete} onClick={() => void download()}>Download {format.toUpperCase()}</button>
    </div>
    <p className="muted prospect-actions-note">Downloads include saved details and collections, up to 100 MiB per file. Filters do not change download scope. Photos are downloaded individually from a business’s details.</p>
    {missing.length > 0 && <div className="prospect-missing"><div><strong>{missing.length} {missing.length === 1 ? 'business has' : 'businesses have'} no map location.</strong><p>Find and save coordinates using Places. This may incur lookup usage.</p></div><button className="btn" disabled={!!progress || !complete} onClick={() => void resolve(missing)}>Resolve all missing locations</button>{missingSelected.length > 0 && <button className="btn" disabled={!!progress} onClick={() => void resolve(missingSelected)}>Resolve selected ({missingSelected.length})</button>}</div>}
    {progress && <div className="prospect-progress" role="status"><div><strong>{progress.action}</strong><p>{progress.done} of {progress.total} finished. Keep this page open.</p></div><button className="btn" onClick={() => { active.current?.abort(); setMessage('Stopped. You can retry when ready.'); }}>Cancel</button></div>}
    {message && <p role="status">{message}</p>}{error && <p className="notice error" role="alert">{error}</p>}
  </section>;
}
